/**
 * Campaign Detail Page
 * 
 * Detail page for viewing campaign information with tabs:
 * - Overview: Display all campaign details in cards
 * - Usage: Display usage statistics filtered by campaignId
 * - Audit Logs: Display audit logs filtered by campaignId
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Alert, Nav } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Common components
import Breadcrumbs from '../../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../../Common/ShimmerLoader';
import { SuccessModal } from '../../../../Common/SuccessModal';
import { CampaignStatusBadge } from '../../../../Common/BadgeStyles';

// API and types
import { getCampaignById, updateCampaignStatus } from '../../../../api/promos';
import { Campaign, CampaignStatus } from '../../../../api/promos/types';

// Tab components
import OverviewTab from './components/OverviewTab';
import { UsageTab, AuditLogsTab } from '../../../../components/promos/detailTabs';

// Styles
import './CampaignDetail.css';
import { hasAnyPermission, type User } from '../../../../utils/roleBasedAccess';

const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'promo.admin',
    'promo.campaigns.manage',
  ]);
  
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Fetch campaign data
  useEffect(() => {
    const fetchCampaign = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        const campaignData = await getCampaignById(id);
        setCampaign(campaignData);
      } catch (err: any) {
        setError(err.message || 'Failed to load campaign details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCampaign();
  }, [id]);
  
  // Handle edit navigation
  const handleEdit = () => {
    if (!hasManagePermissions) return;
    if (campaign) {
      navigate(`/promos/campaigns/${campaign.id}/edit`, {
        state: { fromDetail: true }
      });
    }
  };
  
  // Handle activate campaign
  const handleActivate = async () => {
    if (!hasManagePermissions) return;
    if (!campaign) return;
    
    try {
      setError(null);
      await updateCampaignStatus(campaign.id, CampaignStatus.ACTIVE);
      
      // Refresh campaign data
      const updatedCampaign = await getCampaignById(campaign.id);
      setCampaign(updatedCampaign);
      
      setSuccessTitle('Campaign Activated');
      setSuccessMessage(`Campaign "${campaign.name}" has been activated successfully.`);
      setShowSuccessModal(true);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.error || 
                          err?.message || 
                          'Failed to activate campaign. Please try again.';
      setError(errorMessage);
    }
  };
  
  // Handle pause campaign
  const handlePause = async () => {
    if (!hasManagePermissions) return;
    if (!campaign) return;
    
    try {
      setError(null);
      await updateCampaignStatus(campaign.id, CampaignStatus.PAUSED);
      
      // Refresh campaign data
      const updatedCampaign = await getCampaignById(campaign.id);
      setCampaign(updatedCampaign);
      
      setSuccessTitle('Campaign Paused');
      setSuccessMessage(`Campaign "${campaign.name}" has been paused successfully.`);
      setShowSuccessModal(true);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.error || 
                          err?.message || 
                          'Failed to pause campaign. Please try again.';
      setError(errorMessage);
    }
  };
  
  // Handle archive campaign
  const handleArchive = async () => {
    if (!hasManagePermissions) return;
    if (!campaign) return;
    
    try {
      setError(null);
      await updateCampaignStatus(campaign.id, CampaignStatus.ARCHIVED);
      
      // Refresh campaign data
      const updatedCampaign = await getCampaignById(campaign.id);
      setCampaign(updatedCampaign);
      
      setSuccessTitle('Campaign Archived');
      setSuccessMessage(`Campaign "${campaign.name}" has been archived successfully.`);
      setShowSuccessModal(true);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.error || 
                          err?.message || 
                          'Failed to archive campaign. Please try again.';
      setError(errorMessage);
    }
  };
  
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <ShimmerLoader type="table" rows={5} />
        </Container>
      </div>
    );
  }
  
  if (error || !campaign) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert variant="danger" className="m-3">
            {error || 'Campaign not found'}
          </Alert>
        </Container>
      </div>
    );
  }
  
  return (
    <div className="page-content campaign-detail-page">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Promos', onClick: () => navigate('/promos') },
              { label: 'Campaigns', onClick: () => navigate('/promos/campaigns') },
              { label: campaign.name || 'Campaign Detail', active: true }
            ]} 
          />
        </div>
        
        {/* Header */}
        <div className="mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-3 mb-2">
                <button
                  onClick={() => navigate('/promos/campaigns')}
                  className="btn btn-link p-0 text-decoration-none"
                  style={{ color: '#6B7280', fontSize: '20px' }}
                >
                  <i className="ph-arrow-left"></i>
                </button>
                <h1 style={{ fontWeight: 600, fontSize: 24, marginBottom: 0, lineHeight: 1.2 }}>
                  {campaign.name}
                </h1>
                <CampaignStatusBadge status={campaign.status as 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'EXPIRED' | 'SCHEDULED' | 'INACTIVE'} />
              </div>
              <div className="ms-5" style={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                {campaign.campaignType} • {campaign.startAt ? new Date(campaign.startAt).toLocaleDateString('en-GB') : 'N/A'} → {campaign.endAt ? new Date(campaign.endAt).toLocaleDateString('en-GB') : 'N/A'}
              </div>
            </div>
            
            {/* Action Buttons - hidden for view-only permissions */}
            {hasManagePermissions && (
              <div className="d-flex gap-2">
                <button
                  onClick={handleEdit}
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  style={{
                    height: '40px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  <i className="ph-pencil"></i>
                  Edit
                </button>
                
                {/* Conditional buttons based on status - matching edit page */}
                {campaign.status === CampaignStatus.DRAFT && (
                  <button
                    onClick={handleActivate}
                    className="btn btn-primary d-flex align-items-center gap-2"
                    style={{
                      height: '40px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    <i className="ph-play"></i>
                    Activate Now
                  </button>
                )}
                
                {campaign.status === CampaignStatus.ACTIVE && (
                  <button
                    onClick={handlePause}
                    className="btn d-flex align-items-center gap-2"
                    style={{
                      height: '40px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 500,
                      backgroundColor: '#DC2626',
                      borderColor: '#DC2626',
                      color: '#FFFFFF',
                    }}
                  >
                    <i className="ph-pause"></i>
                    Pause
                  </button>
                )}
                
                {campaign.status === CampaignStatus.PAUSED && (
                  <>
                    <button
                      onClick={handleActivate}
                      className="btn btn-primary d-flex align-items-center gap-2"
                      style={{
                        height: '40px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      <i className="ph-play"></i>
                      Activate
                    </button>
                    <button
                      onClick={handleArchive}
                      className="btn d-flex align-items-center gap-2"
                      style={{
                        height: '40px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        backgroundColor: '#DC2626',
                        borderColor: '#DC2626',
                        color: '#FFFFFF',
                      }}
                    >
                      <i className="ph-archive"></i>
                      Archive
                    </button>
                  </>
                )}
                
                {(campaign.status === CampaignStatus.INACTIVE || 
                  campaign.status === CampaignStatus.SCHEDULED || 
                  campaign.status === CampaignStatus.ARCHIVED ||
                  campaign.status === CampaignStatus.EXPIRED) && (
                  <button
                    onClick={handleArchive}
                    className="btn d-flex align-items-center gap-2"
                    style={{
                      height: '40px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 500,
                      backgroundColor: '#DC2626',
                      borderColor: '#DC2626',
                      color: '#FFFFFF',
                    }}
                    disabled={campaign.status === CampaignStatus.ARCHIVED || campaign.status === CampaignStatus.EXPIRED}
                  >
                    <i className="ph-archive"></i>
                    Archive
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Tabs */}
        <div style={{ padding: '0 15px' }}>
          <Nav variant="tabs" className="tournament-tabs mb-4">
            <Nav.Item>
              <Nav.Link 
                className="tournament-tab"
                active={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                className="tournament-tab"
                active={activeTab === 'usage'}
                onClick={() => setActiveTab('usage')}
              >
                Usage
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                className="tournament-tab"
                active={activeTab === 'audit-logs'}
                onClick={() => setActiveTab('audit-logs')}
              >
                Audit Logs
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>
        
        {/* Tab Content */}
        <div style={{ padding: '0 15px' }}>
          {activeTab === 'overview' && <OverviewTab campaign={campaign} />}
          {activeTab === 'usage' && <UsageTab campaignId={campaign.id} emptyMessage="No usage data found for this campaign." />}
          {activeTab === 'audit-logs' && <AuditLogsTab campaignId={campaign.id} emptyMessage="No audit logs found for this campaign." />}
        </div>
        
        {/* Error Alert */}
        {error && (
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </div>
        )}
        
        {/* Success Modal */}
        <SuccessModal
          show={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          title={successTitle}
          message={successMessage}
        />
      </Container>
    </div>
  );
};

export default CampaignDetailPage;
